import { monthsArray } from "@Components/DatePicker/helper";
import { addDurationToDate } from "@Components/LeadForm/utils";
import BookFormDispatcher from "@Pages/BookForm/action";
import isAvailable from "@Pages/BookForm/components/AccommodationDetails/helper";
import initiateBookformAPI from "@Pages/BookForm/data/initiateBookFormAPI";
import { getAccommodationFormDefaultValue, updateInitiateBookform } from "@Pages/BookForm/utils";
import { formatDate } from "@Pages/Payment/utils";
import {
  getAvailableStartingPricing,
  getPricingDuration,
} from "@Pages/Search/components/SearchListItem/helper";
import { AppState } from "@Store/index";
import AmberlocalStorage from "@Utils/AmberlocalStorage";
import checkIsFlexibleMoveIn from "@Utils/checkIsFlexibleMoveIn";
import { getNonFlexibleTenancyDuration } from "@Utils/getDuration";
import getMoveIn from "@Utils/getMoveIn";
import config from "@Config/index";
import { renderChinaNamedRange } from "@Utils/cnDisplayDate";
import { PAGE } from "constants/configConstants";
import getCookie from "helpers/getCookie";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import useIsDesktop from "hooks/useIsDesktop";
import useTranslationHook from "hooks/useTranslationHook";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

const useAccomodationCard = (
  onlyView = false,
  addStepSummary: any = null,
  handleUpdateInventoryCardSummary: any = null,
  moveToStep: any = null,
  isActiveStep: any = false,
  showOnlyInventoryCardSummary = false,
  isPaymentPage = false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  updateFirstTime = () => {},
) => {
  const {
    accommodationDetails,
    inventoryData,
    priceSummary,
    isUserLoggedIn,
    defaultState,
    book_form_completed,
    book_form_initiated,
    isLFOneFilled,
  } = useSelector(({ bookFormData, appData, userHistory, leadFormModal }: AppState) => ({
    accommodationDetails: bookFormData?.formData?.accommodationDetails,
    inventoryData: bookFormData?.data,
    priceSummary: {
      ...bookFormData?.priceSummary,
      payment_info:
        bookFormData?.priceSummary?.payment_info?.length > 0
          ? bookFormData?.priceSummary?.payment_info
          : bookFormData?.paymentInfo?.payment_info,
    },
    defaultState: bookFormData.initialFromState,
    isUserLoggedIn: appData.isUserLoggedIn,
    book_form_completed: userHistory.book_form_completed,
    book_form_initiated: userHistory.book_form_initiated,
    isLFOneFilled: leadFormModal.isLFOneFilled,
  }));
  const { t } = useTranslationHook();
  const isFlexibleDuration = accommodationDetails?.duration === "flexible";
  const isHMO =
    inventoryData?.meta?.unit_type === "independent_house" &&
    inventoryData?.meta?.inventory_type === "entire_place";
  const countryName = inventoryData?.location?.country?.long_name || "";
  const isUSA = countryName === "United States";
  const [isCalendarOpened, setIsCalendarOpened] = useState(false);
  const [showEditView, setShowEditView] = useState(false);
  const [errors, setErrors] = useState({
    duration: "",
    moveInDate: "",
  });
  const isFirstTimeInitiateCall = useRef(true);
  const isFirstTimeFireEvent = useRef(true);
  const [isLoading, setIsLoading] = useState(true);
  const [formValues, setFormValues] = useState({
    custom_duration: "",
    arrival_date: "",
  });
  const locationName = inventoryData?.location?.locality?.long_name || "";
  const dispatch = useDispatch();
  const availableRooms = isHMO
    ? inventoryData
    : inventoryData?.active_children?.filter((roomChild: any) => isAvailable(roomChild));
  const roomTypeOptions =
    !isHMO &&
    availableRooms?.reduce((acc: Record<number, string>, roomChild: any) => {
      acc[roomChild.id] = roomChild.name;
      return acc;
    }, {});
  const isFlexibleMoveIn = checkIsFlexibleMoveIn(accommodationDetails, availableRooms);
  const nonFlexibleTenancyDuration = getNonFlexibleTenancyDuration(
    accommodationDetails,
    inventoryData,
  );
  const isDesktop = useIsDesktop();

  const getMoveOutDate = ({
    arrival_date = "",
    custom_duration = "",
    roomType = "",
    inventory_id = "",
    duration = "",
  }) => {
    if (parseInt(custom_duration, 10) >= (isUSA ? 49 : 209)) return arrival_date;
    const room = !isHMO && availableRooms?.find((r: any) => +r.id === +roomType);
    if (arrival_date) {
      const durationVal = isFlexibleDuration
        ? +custom_duration?.split(" ")[0]
        : +duration?.split(" ")[0];
      // eslint-disable-next-line no-nested-ternary
      const durationUnit = isFlexibleDuration
        ? isUSA
          ? "month"
          : "week"
        : accommodationDetails?.duration?.split(" ")[1];
      const moveInDate = new Date(arrival_date);
      const moveOutDate = addDurationToDate(moveInDate, durationVal, durationUnit);
      const date = moveOutDate.getDate();
      const month = moveOutDate.getMonth();
      const year = moveOutDate.getFullYear();

      return `${monthsArray[month]} ${date}, ${year}`;
    }
    const tenancy = room?.active_children?.find((ten: any) => +ten.id === +inventory_id);
    if (tenancy) {
      return (
        tenancy?.meta?.available_to_formatted || formatDate(tenancy?.meta?.available_to, true) || ""
      );
    }
    return "";
  };

  const updateSummary = (data: any) => {
    const inventoryCardSummary = getInventoryCardSummary(data);
    if (
      isDesktop &&
      isActiveStep &&
      inventoryCardSummary?.roomName &&
      inventoryCardSummary?.duration &&
      inventoryCardSummary.moveInDate
    ) {
      if (typeof addStepSummary !== "undefined") {
        const moveInLegacy = `${inventoryCardSummary.moveInDate} - ${inventoryCardSummary.moveOutDate}`;
        const stepSummary = {
          roomName: inventoryCardSummary?.roomName,
          duration: inventoryCardSummary?.duration,
          moveIn: config.IS_CHINA_APP
            ? renderChinaNamedRange(
                inventoryCardSummary.moveInDate,
                inventoryCardSummary.moveOutDate,
                moveInLegacy,
              )
            : moveInLegacy,
        };
        addStepSummary?.(stepSummary);
      }
      if (typeof handleUpdateInventoryCardSummary !== "undefined")
        handleUpdateInventoryCardSummary?.({ cardSummary: inventoryCardSummary });
      if (typeof moveToStep !== "undefined") moveToStep?.("", false);
      if (updateFirstTime) updateFirstTime();
    }
  };

  const saveData = (
    {
      roomType = "",
      duration = "",
      inventory_id = "",
      moveOutDate = "",
      arrival_date = "",
      custom_duration = "",
    },
    isOnlyUpdateSummary = false,
  ) => {
    const curentRoom = !isHMO && availableRooms?.find((room: any) => +room.id === +roomType);
    const tenancy = curentRoom?.active_children?.find(
      (ten: any) => inventory_id === `${curentRoom.id}${ten.id}` || +ten.id === +inventory_id,
    );
    const currentTenancyId = tenancy ? tenancy.id : "";
    const formData: any = {
      roomType: +roomType,
      duration,
      moveOutDate,
    };

    if (arrival_date) {
      formData["arrival_date"] = arrival_date;

      // adding try catch block here - previously storing string value in local storage
      // parsing string value ---> error
      const savedArrivalDate: any = AmberlocalStorage.getItem("BOOK_FORM_ARRIVAL_DATE");
      try {
        const parsedSavedArrivalDate = JSON.parse(savedArrivalDate);
        AmberlocalStorage.setItem(
          "BOOK_FORM_ARRIVAL_DATE",
          JSON.stringify({ ...parsedSavedArrivalDate, [inventoryData.id]: arrival_date }),
        );
      } catch (error) {
        AmberlocalStorage.removeItem("BOOK_FORM_ARRIVAL_DATE");
        AmberlocalStorage.setItem(
          "BOOK_FORM_ARRIVAL_DATE",
          JSON.stringify({ [inventoryData.id]: arrival_date }),
        );
      }
    } else {
      formData["arrival_date"] = "";
    }

    if (custom_duration) {
      AmberlocalStorage.setItem("BOOK_FORM_CUSTOM_DURATION", custom_duration);
      formData["custom_duration"] = `${custom_duration} ${isUSA ? t("month") : t("week")}`;
    } else {
      formData["custom_duration"] = "";
    }

    formData["inventory_id"] =
      inventory_id === "flexible" || inventory_id === roomType ? +roomType : +inventory_id;

    if (+inventory_id !== +roomType && inventory_id !== "flexible") {
      formData.tenancyId = +currentTenancyId || +inventory_id;
    }

    const newFormData = {
      ...formData,
    };

    updateSummary({ ...dispatch(getUpdatedAccDetails()), ...newFormData });

    if (!isOnlyUpdateSummary) updateEditView(newFormData);

    const bookFormDispatcher = new BookFormDispatcher(dispatch);
    bookFormDispatcher.updateFormData({
      key: "accommodationDetails",
      formData: newFormData,
    });

    // do initiate api call and add reducer state update
    const isCookiePresent = getCookie("user_flow_token");
    if (!isPaymentPage && isFirstTimeInitiateCall.current && (isCookiePresent || isUserLoggedIn)) {
      isFirstTimeInitiateCall.current = false;
      if (formData && formData["inventory_id"]) {
        const currentInventoryId =
          formData["inventory_id"] === `${+roomType}${currentTenancyId}`
            ? currentTenancyId
            : formData["inventory_id"];

        initiateBookformAPI(
          currentInventoryId,
          `accommodation_card with roomtype - ${roomType} and tenancy - ${currentTenancyId}`,
        );
      }
    }

    const selectedInventory = tenancy || curentRoom;
    if (
      !isPaymentPage &&
      selectedInventory?.id &&
      !book_form_completed.includes(selectedInventory.id) &&
      !book_form_initiated.includes(selectedInventory.id)
    )
      updateInitiateBookform({
        dispatch,
        selectedInventory,
        inventory: inventoryData,
        isTenancy: Boolean(tenancy),
        selectedRoomHavingImage: curentRoom || inventoryData,
      });
  };

  const getUpdatedAccDetails = () => (_: any, getState: any) => {
    const updatedAccommodationDetails = getState()?.bookFormData?.formData?.accommodationDetails;
    return updatedAccommodationDetails;
  };

  const handleOnSave = (isOnlyUpdateSummary = false) => {
    saveData({ ...dispatch(getUpdatedAccDetails()), ...formValues }, isOnlyUpdateSummary);

    gtagEventEmitter({
      action: "card_save",
      category: isPaymentPage ? PAGE.PAYMENTFORM : PAGE.BOOKFORM,
      label: `Accommodation Details Card - ${countryName}`,
      value: 1,
      page: isPaymentPage ? PAGE.PAYMENTFORM : PAGE.BOOKFORM,
      location: locationName,
      propertyName: inventoryData.name,
      isLFOneFilled,
    });
    setShowEditView(false);
  };

  const getCRMGenratedLinkMoveIn = (arrival_date: string) => {
    const arrivalDate = new Date(arrival_date);
    const date = arrivalDate.getDate();
    const month = arrivalDate.getMonth();
    const year = arrivalDate.getFullYear();
    const moveInDate = `${monthsArray[month]} ${date}, ${year}`;
    return moveInDate;
  };

  const getStoredArrivalDate = () => {
    const savedArrivalDate: any = AmberlocalStorage.getItem("BOOK_FORM_ARRIVAL_DATE");
    try {
      const parsedSavedArrivalDate = JSON.parse(savedArrivalDate);
      return parsedSavedArrivalDate[inventoryData.id] || null;
    } catch (error) {
      return null;
    }
  };

  const updateAccomodationDetails = () => {
    const queryParams = new URLSearchParams(window?.location?.search);
    const updatedDefaultState: any = {};
    for (const [key, value] of queryParams.entries()) {
      updatedDefaultState[key] = value;
    }

    const defaultFormData: any = getAccommodationFormDefaultValue({
      defaultState: updatedDefaultState,
      roomTypeOptions,
    });

    const formData = defaultFormData;
    const isOtherCase = +defaultFormData.inventory_id === +defaultFormData.roomType;
    const isDurationFlexible =
      +defaultFormData.inventory_id === +defaultFormData.roomType ||
      defaultFormData.duration === "flexible";

    formData["duration"] =
      // eslint-disable-next-line no-nested-ternary
      isOtherCase && defaultFormData.duration
        ? defaultFormData.duration
        : isDurationFlexible
        ? "flexible"
        : `${getNonFlexibleTenancyDuration(formData, inventoryData)}`;

    formData["arrival_date"] =
      (isOtherCase && defaultFormData.arrival_date) ||
      (isDurationFlexible && defaultFormData.arrival_date)
        ? defaultFormData.arrival_date
        : getStoredArrivalDate();

    //  this will only in case of crm generated link with flexi move in
    if (!formData["arrival_date"] && defaultState?.arrival_date)
      formData["arrival_date"] = getCRMGenratedLinkMoveIn(defaultState?.arrival_date);

    formData["custom_duration"] = isDurationFlexible
      ? defaultFormData.custom_duration || AmberlocalStorage.getItem("BOOK_FORM_CUSTOM_DURATION")
      : "";

    if (isOtherCase) formData["inventory_id"] = defaultFormData.roomType;

    saveData(formData);
    setFormValues(formData);
    setIsLoading(false);
  };

  const updateEditView = (formData: any) => {
    const isDurationFlexible = formData.duration === "flexible";
    let showEdit = false;
    if (checkIsFlexibleMoveIn(formData, availableRooms) && !formData.arrival_date) {
      showEdit = true;
    }
    if (!formData["roomType"]) {
      showEdit = true;
    }
    if (isDurationFlexible && !formData.custom_duration) {
      showEdit = true;
    }
    handleSetShowEditView(showEdit);
    if (!showEdit && isFirstTimeFireEvent?.current) {
      isFirstTimeFireEvent.current = false;
    }
    setIsLoading(false);
  };

  // Re-run when inventory arrives — on SPA nav from property page, this effect used to fire
  // once with empty active_children, so roomType/tenancy never prefilling until hard refresh.
  useEffect(() => {
    const inventoryReady = Boolean(inventoryData?.canonical_name || inventoryData?.id);
    if (!inventoryReady) return;

    const queryParams = new URLSearchParams(window?.location?.search);
    const updatedDefaultState: any = {};
    for (const [key, value] of queryParams.entries()) {
      updatedDefaultState[key] = value;
    }

    const isNotSameRoom =
      updatedDefaultState.inventory_id &&
      +updatedDefaultState?.inventory_id !== +accommodationDetails?.inventory_id;

    if (!onlyView) {
      if (!updatedDefaultState.inventory_id || isNotSameRoom) {
        if (updatedDefaultState.payment_state === "failed") setIsLoading(false);
        else updateAccomodationDetails();
      } else if (+updatedDefaultState?.inventory_id === +accommodationDetails?.inventory_id) {
        updateEditView(accommodationDetails || {});
      }
    } else {
      if ((!updatedDefaultState.inventory_id || isNotSameRoom) && !showOnlyInventoryCardSummary) {
        updateAccomodationDetails();
      }
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init when book-form inventory becomes available
  }, [inventoryData?.canonical_name, inventoryData?.id]);

  const nationalUnit = isUSA ? t("month") : t("week");

  const room =
    !isHMO && availableRooms?.find((r: any) => +r.id === +accommodationDetails?.roomType);
  const pricingConfig =
    accommodationDetails?.inventory_id === "flexible" ||
    +accommodationDetails?.inventory_id === +accommodationDetails?.roomType
      ? room
      : room?.active_children?.find((ten: any) => +ten.id === +accommodationDetails?.tenancyId);

  const durationUnit = isFlexibleDuration
    ? nationalUnit
    : accommodationDetails?.duration?.split(" ")[1];

  const getInventoryCardSummary = (formData: any) => {
    const summary: any = {
      roomName: roomTypeOptions && roomTypeOptions[formData?.roomType],
      duration:
        formData?.custom_duration ||
        (formData?.duration === "flexible" ? "" : formData?.duration || nonFlexibleTenancyDuration),
      moveInDate: getMoveIn(formData, inventoryData),
      moveOutDate: getMoveOutDate(formData || {}),
      price: !pricingConfig?.pricing
        ? null
        : `${t("From")} ${getAvailableStartingPricing(pricingConfig?.pricing)}/${
            isFlexibleDuration
              ? durationUnit
              : `${t(getPricingDuration(pricingConfig?.pricing?.duration))}`
          }`,
      isPending: formData?.duration === "flexible" || Boolean(formData?.arrival_date),
    };
    return summary;
  };

  const inventoryCardSummary = getInventoryCardSummary(accommodationDetails);

  const handleSetShowEditView = (val: boolean) => {
    setShowEditView(val);
  };

  return {
    isFlexibleDuration,
    inventoryCardSummary,
    inventoryData,
    isHMO,
    isUSA,
    setIsCalendarOpened,
    isCalendarOpened,
    showEditView,
    setShowEditView: handleSetShowEditView,
    errors,
    setErrors,
    formValues,
    setFormValues,
    getMoveOutDate,
    handleOnSave,
    isLoading,
    priceSummary,
    isFlexibleMoveIn,
    accommodationDetails,
  };
};

export default useAccomodationCard;
